import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { checkForDesktopUpdate, useDesktopUpdate } from '@/desktop';
import { LicenseSection, readLicense } from '@/features/license';
import { isStandaloneWebApp, isTauri } from '@/lib/platform';

import { useShortcutsDialog } from '../shortcuts-dialog-store';

/**
 * Version, licence, and the data-handling note `PLAN.md` §6 Phase 7 asks for.
 *
 * The note here is the short, factual form: what is stored where, and what
 * leaves the device and when. `docs/PRIVACY.md` is the long form with the
 * reasoning, and the two must say the same thing — when a network request is
 * added to the app, both change.
 */

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-border bg-surface rounded-container border p-4">
      <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
      <div className="mt-2 text-sm leading-relaxed">{children}</div>
    </section>
  );
}

const DATA_NOTES: readonly string[] = [
  'Flights, logbook entries, aircraft and settings are stored locally on this device.',
  'No account, analytics or server storage.',
  'Map tiles are requested from OpenFreeMap, and from Esri and Amazon Web Services for satellite and terrain. These requests include the area shown.',
  'Flight data sources are contacted only when a search or fetch is run. API keys are sent only to their own source.',
  'A relay, when set, receives requests to flight data sources.',
  'Backup and sync files contain all data except API keys. They are stored where they are saved.',
  'iCloud sync, when signed in, stores sync files in the private iCloud database.',
];

function AboutRoute() {
  const desktop = isTauri();
  const manualResult = useDesktopUpdate((state) => state.lastManualResult);
  const available = useDesktopUpdate((state) => state.available);

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4">
      <h1 className="text-lg font-semibold tracking-tight">About</h1>

      <Section title="Flight Data Viewer">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <dt className="text-muted-foreground">Version</dt>
          <dd className="tabular">{__APP_VERSION__}</dd>
          <dt className="text-muted-foreground">Runs as</dt>
          <dd>
            {desktop ? 'Desktop app' : isStandaloneWebApp() ? 'Installed web app' : 'Browser'}
          </dd>
        </dl>
        {desktop ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void readLicense().then((state) =>
                  checkForDesktopUpdate({
                    manual: true,
                    license: state.status === 'valid' ? state.license : null,
                  }),
                );
              }}
            >
              Check for updates
            </Button>
            <span className="text-muted-foreground text-xs" role="status">
              {available !== null
                ? `Version ${available.version} available.`
                : manualResult === 'none'
                  ? 'Up to date.'
                  : manualResult === 'error'
                    ? 'Update check failed.'
                    : ''}
            </span>
          </div>
        ) : null}
        <p className="text-muted-foreground mt-3 text-xs">
          The bound logbook remains the legal record.
        </p>
      </Section>

      <LicenseSection />

      <Section title="Privacy and data">
        <ul className="text-muted-foreground list-disc space-y-1 pl-5">
          {DATA_NOTES.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </Section>

      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            useShortcutsDialog.setState({ open: true });
          }}
        >
          Keyboard shortcuts
        </Button>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/about')({
  component: AboutRoute,
});
